/**
 * Company Search — first-visit state (sealed state 1), with the two sealed
 * rails now live via get_company_lookup_activity.
 *
 * The centered hero (search glyph, "Look up a company", research-and-brief
 * subline, and the search) renders immediately; the "Recent lookups" (deduped,
 * relative time) and "Trending internally" (orgs with their live signal_count)
 * rails load asynchronously below it and never block the search. Clicking a
 * rail entry runs that lookup. Each rail shows an honest empty state when its
 * array is empty (sparse data is expected for a new feature).
 */

import { useLookupActivity } from "../hooks";
import { relativeTime } from "../logic";
import { SearchBar } from "./SearchBar";
import { IconSearch } from "./icons";

export function FirstVisitHero({ onSubmit }: { onSubmit: (query: string) => void }): JSX.Element {
  const activity = useLookupActivity();
  const now = Date.now();
  const recent = activity.data?.recent ?? [];
  const trending = activity.data?.trending ?? [];

  return (
    <div className="sxco-hero">
      <span className="sxco-hero-glyph" aria-hidden="true">
        <IconSearch />
      </span>
      <h2 className="sxco-hero-title">Look up a company</h2>
      <p className="sxco-hero-sub">
        Research any company—whether or not it has a signal or existing BDSE relationship—and generate a Company
        Intelligence Brief.
      </p>
      <div className="sxco-hero-search">
        <SearchBar onSubmit={onSubmit} onClear={() => {}} busy={false} />
      </div>

      <div className="sxco-rails">
        {activity.isError ? (
          <p className="sxco-rail-note">Recent activity is unavailable right now.</p>
        ) : (
          <>
            <section className="sxco-rail">
              <p className="sxco-rail-head">Recent lookups</p>
              {activity.isPending ? (
                <p className="sxco-rail-note">Loading…</p>
              ) : recent.length === 0 ? (
                <p className="sxco-rail-note">No recent lookups yet.</p>
              ) : (
                <ul className="sxco-rail-list">
                  {recent.map((r) => (
                    <li key={r.organizationId}>
                      <button
                        type="button"
                        className="sxco-rail-item"
                        onClick={() => r.displayName && onSubmit(r.displayName)}
                        disabled={!r.displayName}
                      >
                        <span className="sxco-rail-name">{r.displayName ?? "—"}</span>
                        <span className="sxco-rail-meta">{relativeTime(r.lookedUpAt, now) ?? ""}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="sxco-rail">
              <p className="sxco-rail-head">Trending internally</p>
              {activity.isPending ? (
                <p className="sxco-rail-note">Loading…</p>
              ) : trending.length === 0 ? (
                <p className="sxco-rail-note">No trending companies yet.</p>
              ) : (
                <ul className="sxco-rail-list">
                  {trending.map((t) => (
                    <li key={t.organizationId}>
                      <button
                        type="button"
                        className="sxco-rail-item"
                        onClick={() => t.displayName && onSubmit(t.displayName)}
                        disabled={!t.displayName}
                      >
                        <span className="sxco-rail-name">{t.displayName ?? "—"}</span>
                        <span className="sxco-rail-meta">
                          {t.signalCount} {t.signalCount === 1 ? "signal" : "signals"}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
